import { useMemo } from "react";
import { useUrlFiltersReducer } from "@/hooks/useUrlSyncedFilters";
import {
  booleanCodec,
  dayRangeCodec,
  enumCodec,
  nullableBooleanCodec,
  nullableEnumCodec,
  nullableStringCodec,
  readFiltersFromUrl,
  stringCodec,
  type UrlFilterCodecs,
} from "@/lib/urlFilters.util";
import {
  dayFilter,
  FULL_WEEK_LENGTH,
  getWeekStart,
  monthFilter,
  rangeFilter,
  resolveDateFilterType,
  weekFilter,
  weekNavigationFilter,
  weekStartCodec,
  type DateFilterPatch,
  type DateFilterType,
} from "@/components/backOffice/shared/dateFilter.util";
import { DeliveryStatusEnum } from "@/constants/enums/deliveryStatus.enum";
import { ExpeditionBlockedReasonEnum } from "@/constants/enums/expeditionBlockedReason.enum";
import { ManualTrackingEnum } from "@/constants/enums/manualTracking.enum";
import { PackagingFilterEnum } from "@/constants/enums/packagingFilter.enum";
import { COUNTRY_REGIONS, type RegionCode } from "@/constants/countries";

export type DeliveryStatusGroup = "all" | "ongoing" | "delivered" | "blocked" | "failed";

export type FinalizedDateFilterType = DateFilterType;

/** Transporteur du suivi : `all` = aucun filtre, `fedex` = tout sauf DHL/TNT. */
export type CarrierFilter = "all" | "fedex" | "dhlTnt";

export const CARRIER_FILTERS: CarrierFilter[] = ["all", "fedex", "dhlTnt"];

export type ValidatedExpeditionsFilters = {
  deliveryStatusGroup: DeliveryStatusGroup;
  weekStart: Date;
  search: string;
  searchExact: boolean;
  isExpress: boolean;
  isRemote: boolean;
  isCustomQuotation: boolean;
  isManyBottles: boolean;
  carrier: CarrierFilter;
  isShippingLate: boolean;
  isFinalizedBySelf: boolean;
  finalizedById: string | null;
  country: string | null;
  region: RegionCode | null;
  trackingCode: string;
  customId: string | null;
  packagingType: PackagingFilterEnum | null;
  manualTracking: string | null;
  pickupDate: [Date, Date] | null;
  finalizedAt: [Date, Date] | null;
  finalizedDateFilterType: FinalizedDateFilterType | null;
  blockedManual: boolean | null;
  blockedReason: ExpeditionBlockedReasonEnum | null;
  deletedAt: [Date, Date] | null;
};

type Action =
  | { type: "SET_DELIVERY_STATUS_GROUP"; payload: DeliveryStatusGroup }
  | { type: "SET_SEARCH"; payload: string }
  | { type: "TOGGLE_EXPRESS" }
  | { type: "TOGGLE_REMOTE" }
  | { type: "TOGGLE_CUSTOM_QUOTATION" }
  | { type: "TOGGLE_MANY_BOTTLES" }
  | { type: "SET_CARRIER"; payload: CarrierFilter }
  | { type: "TOGGLE_SHIPPING_LATE" }
  | { type: "TOGGLE_FINALIZED_BY_SELF" }
  | { type: "SET_FINALIZED_BY_ID"; payload: string | null }
  | { type: "SET_COUNTRY"; payload: string | null }
  | { type: "SET_REGION"; payload: RegionCode | null }
  | { type: "SET_TRACKING_CODE"; payload: string }
  | { type: "SET_CUSTOM_ID"; payload: string | null }
  | { type: "SET_PACKAGING_TYPE"; payload: PackagingFilterEnum | null }
  | { type: "SET_MANUAL_TRACKING"; payload: string | null }
  | { type: "SET_BLOCKED_MANUAL"; payload: boolean | null }
  | { type: "SET_BLOCKED_REASON"; payload: ExpeditionBlockedReasonEnum | null }
  | { type: "SET_PICKUP_DATE_RANGE"; payload: [Date, Date] | null }
  | { type: "SET_FINALIZED_AT_RANGE"; payload: [Date, Date] | null }
  | { type: "SET_DELETED_AT_RANGE"; payload: [Date, Date] | null }
  | { type: "SET_WEEK_START"; payload: Date }
  | { type: "SET_FINALIZED_DAY"; payload: Date | null }
  | { type: "SET_FINALIZED_WEEK"; payload: { active: boolean; weekStart: Date } }
  | { type: "SET_FINALIZED_MONTH"; payload: { active: boolean; month: Date } }
  | { type: "RESET_ADVANCED_FILTERS" };

const toFinalizedAt = (patch: DateFilterPatch) => ({
  finalizedAt: patch.dateAt,
  finalizedDateFilterType: patch.dateFilterType,
});

const initialState: ValidatedExpeditionsFilters = {
  deliveryStatusGroup: "ongoing",
  weekStart: getWeekStart(new Date()),
  search: "",
  searchExact: false,
  isExpress: false,
  isRemote: false,
  isCustomQuotation: false,
  isManyBottles: false,
  carrier: "all",
  isShippingLate: false,
  isFinalizedBySelf: false,
  finalizedById: null,
  country: null,
  region: null,
  trackingCode: "",
  customId: null,
  packagingType: null,
  manualTracking: null,
  pickupDate: null,
  finalizedAt: null,
  finalizedDateFilterType: null,
  blockedManual: null,
  blockedReason: null,
  deletedAt: null,
};

function reducer(state: ValidatedExpeditionsFilters, action: Action): ValidatedExpeditionsFilters {
  switch (action.type) {
    case "SET_DELIVERY_STATUS_GROUP":
      return { ...state, deliveryStatusGroup: action.payload };
    case "SET_SEARCH":
      return { ...state, search: action.payload, searchExact: false };
    case "TOGGLE_EXPRESS":
      return { ...state, isExpress: !state.isExpress };
    case "TOGGLE_REMOTE":
      return { ...state, isRemote: !state.isRemote };
    case "TOGGLE_CUSTOM_QUOTATION":
      return { ...state, isCustomQuotation: !state.isCustomQuotation };
    case "TOGGLE_MANY_BOTTLES":
      return { ...state, isManyBottles: !state.isManyBottles };
    case "SET_CARRIER":
      return { ...state, carrier: action.payload };
    case "TOGGLE_SHIPPING_LATE":
      return { ...state, isShippingLate: !state.isShippingLate };
    case "TOGGLE_FINALIZED_BY_SELF":
      return { ...state, isFinalizedBySelf: !state.isFinalizedBySelf };
    case "SET_FINALIZED_BY_ID":
      return { ...state, finalizedById: action.payload };
    // `country` and `region` are exclusive: the query gives priority to the country,
    case "SET_COUNTRY":
      return { ...state, country: action.payload, region: action.payload ? null : state.region };
    case "SET_REGION":
      return { ...state, region: action.payload, country: null };
    case "SET_TRACKING_CODE":
      return { ...state, trackingCode: action.payload };
    case "SET_CUSTOM_ID":
      return { ...state, customId: action.payload };
    case "SET_PACKAGING_TYPE":
      return { ...state, packagingType: action.payload };
    case "SET_MANUAL_TRACKING":
      return { ...state, manualTracking: action.payload };
    case "SET_BLOCKED_MANUAL":
      return { ...state, blockedManual: action.payload };
    case "SET_BLOCKED_REASON":
      return { ...state, blockedReason: action.payload };
    case "SET_PICKUP_DATE_RANGE":
      return { ...state, pickupDate: action.payload };
    case "SET_FINALIZED_AT_RANGE":
      return { ...state, ...toFinalizedAt(rangeFilter(action.payload)) };
    case "SET_DELETED_AT_RANGE":
      return { ...state, deletedAt: action.payload };
    case "SET_WEEK_START": {
      const weekStart = action.payload;
      const patch = weekNavigationFilter(
        state.finalizedDateFilterType,
        weekStart,
        FULL_WEEK_LENGTH,
      );
      return { ...state, weekStart, ...(patch ? toFinalizedAt(patch) : {}) };
    }
    case "SET_FINALIZED_DAY":
      return { ...state, ...toFinalizedAt(dayFilter(action.payload)) };
    case "SET_FINALIZED_WEEK":
      return {
        ...state,
        ...toFinalizedAt(
          weekFilter(action.payload.active, action.payload.weekStart, FULL_WEEK_LENGTH),
        ),
      };
    case "SET_FINALIZED_MONTH":
      return {
        ...state,
        ...toFinalizedAt(monthFilter(action.payload.active, action.payload.month)),
      };
    case "RESET_ADVANCED_FILTERS":
      return {
        ...initialState,
        weekStart: getWeekStart(new Date()),
        deliveryStatusGroup: state.deliveryStatusGroup,
      };
    default:
      return state;
  }
}

export function carrierToParams(carrier: CarrierFilter): { isDhlTnt?: boolean } {
  switch (carrier) {
    case "dhlTnt":
      return { isDhlTnt: true };
    case "fedex":
      return { isDhlTnt: false };
    case "all":
    default:
      return {};
  }
}

export function deliveryStatusGroupToParams(group: DeliveryStatusGroup): {
  deliveryStatus?: string[];
} {
  switch (group) {
    case "ongoing":
      return {
        deliveryStatus: [
          DeliveryStatusEnum.ONGOING,
          DeliveryStatusEnum.WAITING,
          DeliveryStatusEnum.PARTIALLY_DELIVERED,
        ],
      };
    case "delivered":
      return {
        deliveryStatus: [DeliveryStatusEnum.DELIVERED, DeliveryStatusEnum.TOTALLY_DELIVERED],
      };
    case "blocked":
      return { deliveryStatus: [DeliveryStatusEnum.BLOCKED] };
    case "failed":
      return { deliveryStatus: [DeliveryStatusEnum.FAILED] };
    default:
      return {};
  }
}

const VALID_GROUPS: DeliveryStatusGroup[] = ["all", "ongoing", "delivered", "failed"];
const FINALIZED_DATE_FILTER_TYPES: FinalizedDateFilterType[] = ["DAY", "WEEK", "MONTH", "RANGE"];
const BLOCKED_REASONS = Object.values(ExpeditionBlockedReasonEnum);
const MANUAL_TRACKING_VALUES: string[] = Object.values(ManualTrackingEnum);
const PACKAGING_TYPES = Object.values(PackagingFilterEnum);
const REGION_CODES = Object.keys(COUNTRY_REGIONS) as RegionCode[];

const FILTER_CODECS: UrlFilterCodecs<ValidatedExpeditionsFilters> = {
  deliveryStatusGroup: enumCodec("group", VALID_GROUPS, initialState.deliveryStatusGroup),
  weekStart: weekStartCodec(),
  search: stringCodec("search"),
  searchExact: booleanCodec("exact"),
  isExpress: booleanCodec("express"),
  isRemote: booleanCodec("remote"),
  isCustomQuotation: booleanCodec("customQuotation"),
  isManyBottles: booleanCodec("manyBottles"),
  carrier: enumCodec("carrier", CARRIER_FILTERS, initialState.carrier),
  isShippingLate: booleanCodec("shippingLate"),
  isFinalizedBySelf: booleanCodec("finalizedBySelf"),
  finalizedById: nullableStringCodec("finalizedBy"),
  country: nullableStringCodec("country"),
  region: nullableEnumCodec("region", REGION_CODES),
  trackingCode: stringCodec("trackingCode"),
  customId: nullableStringCodec("customId"),
  packagingType: nullableEnumCodec("packaging", PACKAGING_TYPES),
  manualTracking: nullableStringCodec("tracking", MANUAL_TRACKING_VALUES),
  pickupDate: dayRangeCodec("pickupRange"),
  finalizedAt: dayRangeCodec("finalized"),
  finalizedDateFilterType: nullableEnumCodec("finalizedType", FINALIZED_DATE_FILTER_TYPES),
  blockedManual: nullableBooleanCodec("blockedManual"),
  blockedReason: nullableEnumCodec("blockedReason", BLOCKED_REASONS),
  deletedAt: dayRangeCodec("deletedRange"),
};

function getInitialStateFromUrl(searchParams: URLSearchParams): ValidatedExpeditionsFilters {
  const defaults = { ...initialState, weekStart: getWeekStart(new Date()) };
  const state = readFiltersFromUrl(searchParams, defaults, FILTER_CODECS);
  if (state.country) state.region = null;
  state.finalizedDateFilterType = resolveDateFilterType(
    state.finalizedAt,
    state.finalizedDateFilterType,
  );
  return state;
}

export function useValidatedExpeditionsFilters() {
  const { filters, debouncedSearch, dispatch } = useUrlFiltersReducer(
    reducer,
    FILTER_CODECS,
    getInitialStateFromUrl,
  );

  const actions = useMemo(
    () => ({
      setDeliveryStatusGroup: (v: DeliveryStatusGroup) =>
        dispatch({ type: "SET_DELIVERY_STATUS_GROUP", payload: v }),
      setSearch: (v: string) => dispatch({ type: "SET_SEARCH", payload: v }),
      toggleExpress: () => dispatch({ type: "TOGGLE_EXPRESS" }),
      toggleRemote: () => dispatch({ type: "TOGGLE_REMOTE" }),
      toggleCustomQuotation: () => dispatch({ type: "TOGGLE_CUSTOM_QUOTATION" }),
      toggleManyBottles: () => dispatch({ type: "TOGGLE_MANY_BOTTLES" }),
      setCarrier: (v: CarrierFilter) => dispatch({ type: "SET_CARRIER", payload: v }),
      toggleShippingLate: () => dispatch({ type: "TOGGLE_SHIPPING_LATE" }),
      toggleFinalizedBySelf: () => dispatch({ type: "TOGGLE_FINALIZED_BY_SELF" }),
      setFinalizedById: (v: string | null) => dispatch({ type: "SET_FINALIZED_BY_ID", payload: v }),
      setCountry: (v: string | null) => dispatch({ type: "SET_COUNTRY", payload: v }),
      setRegion: (v: RegionCode | null) => dispatch({ type: "SET_REGION", payload: v }),
      setTrackingCode: (v: string) => dispatch({ type: "SET_TRACKING_CODE", payload: v }),
      setCustomId: (v: string | null) => dispatch({ type: "SET_CUSTOM_ID", payload: v }),
      setPackagingType: (v: PackagingFilterEnum | null) =>
        dispatch({ type: "SET_PACKAGING_TYPE", payload: v }),
      setManualTracking: (v: string | null) =>
        dispatch({ type: "SET_MANUAL_TRACKING", payload: v }),
      setBlockedManual: (v: boolean | null) => dispatch({ type: "SET_BLOCKED_MANUAL", payload: v }),
      setBlockedReason: (v: ExpeditionBlockedReasonEnum | null) =>
        dispatch({ type: "SET_BLOCKED_REASON", payload: v }),
      setPickupDateRange: (v: [Date, Date] | null) =>
        dispatch({ type: "SET_PICKUP_DATE_RANGE", payload: v }),
      setFinalizedAtRange: (v: [Date, Date] | null) =>
        dispatch({ type: "SET_FINALIZED_AT_RANGE", payload: v }),
      setDeletedAtRange: (v: [Date, Date] | null) =>
        dispatch({ type: "SET_DELETED_AT_RANGE", payload: v }),
      setWeekStart: (weekStart: Date) => dispatch({ type: "SET_WEEK_START", payload: weekStart }),
      setFinalizedDay: (v: Date | null) => dispatch({ type: "SET_FINALIZED_DAY", payload: v }),
      setFinalizedWeek: (active: boolean, weekStart: Date) =>
        dispatch({ type: "SET_FINALIZED_WEEK", payload: { active, weekStart } }),
      setFinalizedMonth: (active: boolean, month: Date) =>
        dispatch({ type: "SET_FINALIZED_MONTH", payload: { active, month } }),
      resetAdvancedFilters: () => dispatch({ type: "RESET_ADVANCED_FILTERS" }),
    }),
    [dispatch],
  );

  return useMemo(
    () => ({ filters, debouncedSearch, actions }),
    [filters, debouncedSearch, actions],
  );
}
